import { ENDPOINTS } from '@app/core/constants/endpoints';
import { ACCOUNT_PERMISSION_LEVEL, USER_ROLE_PERMISSION_LEVEL } from '@app/shared/enum/permissions';
import { PageSideNavigationGroup, PageTabNavigationItem } from '@app/shared/models/nav-item.model';
import { DASHBOARDS } from '../enum/dashboards';
import { CUSTOM_API_QUERY } from '@app/shared/enum/custom-api-query';

export enum APP_MENU {
    ACCOUNTS = 'Accounts',
    ADMIN = 'Admin',
    PROFILE_SETTINGS = 'Profile settings',
    MODEL_VIEWER = 'Model viewer',
    MODEL_MANAGER = 'Model manager',
    LOGOUT = 'Logout',
}

export const ADMIN_MENU: PageSideNavigationGroup[] = [
    {
        label: 'Admin',
        items: [
            {
                route: '/admin/general-settings',
                featureFlagKey: 'company',
                label: 'General settings',
                icon: 'business',
                permissions: {
                    accountPermission: ACCOUNT_PERMISSION_LEVEL.ALL,
                    userRolePermission: USER_ROLE_PERMISSION_LEVEL.ALL,
                },
            },
            {
                route: '/admin/customers',
                featureFlagKey: 'customers',
                label: 'Customers',
                icon: 'badge',
                permissions: {
                    accountPermission: ACCOUNT_PERMISSION_LEVEL.SERVICE_ACCOUNT,
                    userRolePermission: USER_ROLE_PERMISSION_LEVEL.ALL,
                },
            },
            {
                route: '/admin/users',
                featureFlagKey: 'users',
                label: 'Users',
                icon: 'person',
                permissions: {
                    accountPermission: ACCOUNT_PERMISSION_LEVEL.ALL,
                    userRolePermission: USER_ROLE_PERMISSION_LEVEL.ALL,
                },
            },
            {
                route: '/admin/user-groups',
                featureFlagKey: 'userGroups',
                label: 'User groups',
                icon: 'groups',
                permissions: {
                    accountPermission: ACCOUNT_PERMISSION_LEVEL.ALL,
                    userRolePermission: USER_ROLE_PERMISSION_LEVEL.ALL,
                },
            },
            {
                route: '/admin/assets',
                featureFlagKey: 'assets',
                label: 'Assets',
                icon: 'precision_manufacturing',
                permissions: {
                    accountPermission: ACCOUNT_PERMISSION_LEVEL.ALL,
                    userRolePermission: USER_ROLE_PERMISSION_LEVEL.ALL,
                },
            },
            {
                route: '/admin/asset-groups',
                featureFlagKey: 'assetGroups',
                label: 'Asset Groups',
                icon: 'dashboard_customize',
                permissions: {
                    accountPermission: ACCOUNT_PERMISSION_LEVEL.ALL,
                    userRolePermission: USER_ROLE_PERMISSION_LEVEL.ALL,
                },
            },
            {
                route: '/admin/shifts',
                featureFlagKey: 'shifts',
                label: 'Shifts',
                icon: 'work_history',
                permissions: {
                    accountPermission: ACCOUNT_PERMISSION_LEVEL.ALL,
                    userRolePermission: USER_ROLE_PERMISSION_LEVEL.ALL,
                },
            },
            {
                route: '/admin/devices',
                featureFlagKey: 'devices',
                label: 'Devices',
                icon: 'electrical_services',
                permissions: {
                    accountPermission: ACCOUNT_PERMISSION_LEVEL.ALL,
                    userRolePermission: USER_ROLE_PERMISSION_LEVEL.ALL,
                },
            },
            {
                route: '/admin/models',
                featureFlagKey: 'models',
                label: 'Models',
                icon: 'hub',
                permissions: {
                    accountPermission: ACCOUNT_PERMISSION_LEVEL.ALL,
                    userRolePermission: USER_ROLE_PERMISSION_LEVEL.ALL,
                },
            },
            {
                route: '/admin/subscriptions',
                featureFlagKey: 'subscriptions',
                label: 'Subscription',
                icon: 'card_membership',
                permissions: {
                    accountPermission: ACCOUNT_PERMISSION_LEVEL.ALL,
                    userRolePermission: USER_ROLE_PERMISSION_LEVEL.ADMIN,
                },
            },
        ],
    },
];

export const DASHBOARD_MENU: PageSideNavigationGroup[] = [
    {
        label: 'Dashboard',
        items: [
            {
                route: '/dashboard/status',
                featureFlagKey: DASHBOARDS.STATUS,
                label: 'Status',
                icon: 'monitor_heart',
                permissions: {
                    accountPermission: ACCOUNT_PERMISSION_LEVEL.ALL,
                    userRolePermission: USER_ROLE_PERMISSION_LEVEL.ALL,
                },
                metadata: {
                    isDynamicDashboard: false,
                },
            },
            {
                route: '/dashboard/explore',
                featureFlagKey: DASHBOARDS.EXPLORE,
                label: 'Explore',
                icon: 'explore',
                permissions: {
                    accountPermission: ACCOUNT_PERMISSION_LEVEL.ALL,
                    userRolePermission: USER_ROLE_PERMISSION_LEVEL.ALL,
                },
                metadata: {
                    isDynamicDashboard: true,
                },
            },
        ],
    },
    {
        label: 'Reports',
        items: [
            {
                route: '/dashboard/smart-reports',
                featureFlagKey: DASHBOARDS.SMART_REPORTS,
                label: 'Smart Reports',
                icon: 'description',
                permissions: {
                    accountPermission: ACCOUNT_PERMISSION_LEVEL.ALL,
                    userRolePermission: USER_ROLE_PERMISSION_LEVEL.ALL,
                },
                childrenRoute: ['/dashboard/smart-reports/settings'],
                metadata: {
                    isDynamicDashboard: false,
                },
            },
            {
                route: '/dashboard/utilization',
                featureFlagKey: DASHBOARDS.UTILIZATION,
                label: 'Utilization',
                icon: 'bar_chart',
                permissions: {
                    accountPermission: ACCOUNT_PERMISSION_LEVEL.ALL,
                    userRolePermission: USER_ROLE_PERMISSION_LEVEL.ALL,
                },
                legacyUrl: ENDPOINTS.urls.dashboard.utilization,
            },
            {
                route: '/dashboard/cost',
                featureFlagKey: DASHBOARDS.COST,
                label: 'Cost',
                icon: 'attach_money',
                permissions: {
                    accountPermission: ACCOUNT_PERMISSION_LEVEL.ALL,
                    userRolePermission: USER_ROLE_PERMISSION_LEVEL.ALL,
                },
                legacyUrl: ENDPOINTS.urls.dashboard.cost,
            },
            {
                route: '/dashboard/alerts',
                featureFlagKey: DASHBOARDS.ALERTS,
                label: 'Alerts',
                icon: 'warning',
                permissions: {
                    accountPermission: ACCOUNT_PERMISSION_LEVEL.ALL,
                    userRolePermission: USER_ROLE_PERMISSION_LEVEL.ALL,
                },
                legacyUrl: ENDPOINTS.urls.dashboard.alerts,
            },
        ],
    },
];

export const DATA_VERIFICATION_TOOL_MENU: PageTabNavigationItem[] = [
    {
        route: '/data-verification-tool/events',
        label: 'Events',
        data: {
            type: CUSTOM_API_QUERY.EVENTS,
        },
    },
    {
        route: '/data-verification-tool/reloads',
        label: 'Reloads',
        data: {
            type: CUSTOM_API_QUERY.RELOADS,
        },
    },
];
